import type { FilaDeCartera } from '@/api/cartera.api';
import { haceCuanto, hora12 } from './athena';
import { fechaLegible, legible } from './cartera';

/**
 * EL PROCESO DE VENTA COMO UNA LÍNEA (tanda 6): en qué paso está cada cliente, qué sigue
 * y cuánto le falta para cerrar. PURO: lo pinta `components/cartera/TarjetaDeProgreso.tsx`.
 *
 * Los pasos son las etapas que el negocio tiene configuradas en su ficha y, al final,
 * «cerrado». Cerrar no es una etapa de la ficha: es la temperatura CERRADO de su chat,
 * y manda sobre la etapa que tenga escrita.
 */

export const CERRADO = 'cerrado';

export interface Paso {
  id: string;
  etiqueta: string;
  /** Nombre del icono de lucide; la tarjeta lo traduce al componente. */
  icono: string;
  sigue: string | null;
}

/** Lo que se sabe de cada etapa de Richard. Una que no esté aquí (la agregaron en la configuración) sale con `Circle` y sin «qué sigue». */
const CONOCIDAS: Record<string, { icono: string; sigue: string }> = {
  nuevo: { icono: 'UserPlus', sigue: 'Calificar: zona, presupuesto y para qué lo quiere' },
  calificado: { icono: 'ClipboardCheck', sigue: 'Agendar el Zoom' },
  zoom_agendado: { icono: 'CalendarClock', sigue: 'Confirmar y hacer el Zoom' },
  zoom_hecho: { icono: 'Video', sigue: 'Coordinar el tour' },
  tour: { icono: 'Building2', sigue: 'Pedir el preapproval' },
  preapproval: { icono: 'FileCheck2', sigue: 'Tomar la reserva' },
  reserva: { icono: 'KeyRound', sigue: 'Cerrar: contrato y pago inicial' },
  [CERRADO]: { icono: 'Trophy', sigue: 'Postventa: referidos y seguimiento' },
};

/** Lo que toca hacer con un cliente que está en esa etapa, o null si la etapa no es de las conocidas. */
export function queSigue(etapa: string): string | null {
  return CONOCIDAS[etapa]?.sigue ?? null;
}

/** Los pasos del negocio: las etapas configuradas, en su orden, y al final «cerrado» (una sola vez). */
export function pasosDe(etapas: readonly string[]): Paso[] {
  const ids = [...new Set([...etapas.filter((e) => e && e !== CERRADO), CERRADO])];
  return ids.map((id) => ({ id, etiqueta: legible(id), icono: CONOCIDAS[id]?.icono ?? 'Circle', sigue: queSigue(id) }));
}

const estaCerrado = (fila: FilaDeCartera) => fila.conversacion?.temperatura === 'CERRADO';

/** Índice del paso en que está. CERRADO (temperatura) → el último. Sin etapa o desconocida → 0. */
export function posicionDe(fila: FilaDeCartera, etapas: readonly string[]): number {
  const pasos = pasosDe(etapas);
  if (estaCerrado(fila)) return pasos.length - 1;
  return Math.max(0, pasos.findIndex((p) => p.id === fila.etapa));
}

/** Cuántos pasos le faltan para cerrar (0 si ya cerró). */
export function faltan(fila: FilaDeCartera, etapas: readonly string[]): number {
  return pasosDe(etapas).length - 1 - posicionDe(fila, etapas);
}

// Richard trabaja en hora de Miami: el panel la muestra así aunque se abra desde otro país.
const ZONA = 'America/New_York';
const DIA_MS = 86_400_000;
const PARTES = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h23',
});

/**
 * La misma fecha con sus campos LOCALES puestos a la hora de pared de Miami. `hora12` lee
 * la hora local del navegador: con esto dice la de Miami, y contar días entre dos de estas
 * fechas da «hoy» y «mañana» de allá.
 */
function enMiami(fecha: Date): Date {
  const p = Object.fromEntries(PARTES.formatToParts(fecha).map((x) => [x.type, Number(x.value)]));
  return new Date(p.year!, p.month! - 1, p.day!, p.hour! % 24, p.minute!);
}

const medianoche = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** «hoy 4:00 p. m.», «mañana 11:00 a. m.», «12 oct 9:30 a. m.». */
function cuando(iso: string, ahora: Date): string {
  const fecha = enMiami(new Date(iso));
  const dias = Math.round((medianoche(fecha) - medianoche(enMiami(ahora))) / DIA_MS);
  const dia = dias === 0 ? 'hoy' : dias === 1 ? 'mañana' : fechaLegible(iso).replace(/ \d{4}$/, '');
  return `${dia} ${hora12(fecha)}`;
}

/** «hace 2 días» → «hace 2 d»: en la tarjeta el plazo va en una esquina. */
const hace = (iso: string, ahora: Date) => haceCuanto(iso, ahora).replace(/ días?$/, ' d');

const recortar = (texto: string, largo: number) => (texto.length > largo ? `${texto.slice(0, largo - 1).trimEnd()}…` : texto);

export interface Plazo {
  texto: string;
  tono: 'normal' | 'alerta' | 'apagado';
}

/**
 * Lo que va donde la captura dice «ETA». Por orden: ya cerró; el Zoom si está en
 * `zoom_agendado`; la próxima acción con fecha (o cuánto lleva vencida); la próxima
 * acción sin fecha; nada.
 */
export function plazoDe(fila: FilaDeCartera, ahora: Date): Plazo {
  if (estaCerrado(fila)) return { texto: 'Cerrado', tono: 'normal' };
  if (fila.etapa === 'zoom_agendado' && fila.zoomEl) {
    // La API manda el Zoom más cercano; si ya pasaron todos, el último: toca marcarlo como hecho.
    return Date.parse(fila.zoomEl) >= ahora.getTime()
      ? { texto: `Zoom: ${cuando(fila.zoomEl, ahora)}`, tono: 'normal' }
      : { texto: `El Zoom fue ${hace(fila.zoomEl, ahora)}`, tono: 'alerta' };
  }
  const proxima = fila.proximaAccion;
  if (proxima?.fecha) {
    return Date.parse(proxima.fecha) >= ahora.getTime()
      ? { texto: `Próximo: ${cuando(proxima.fecha, ahora)}`, tono: 'normal' }
      : { texto: `Vencido ${hace(proxima.fecha, ahora)}`, tono: 'alerta' };
  }
  if (proxima) return { texto: `Próximo: ${recortar(proxima.descripcion.trim(), 28)}`, tono: 'normal' };
  return { texto: 'Sin próxima acción', tono: 'apagado' };
}

/**
 * «hace 3 d en esta etapa», o null si no se sabe desde cuándo. De un cliente cerrado
 * tampoco se sabe: el sello es de la última etapa que se le puso a mano, no del cierre.
 */
export function enEtapaDesde(fila: FilaDeCartera, ahora: Date): string | null {
  if (!fila.etapaDesde || estaCerrado(fila)) return null;
  const t = hace(fila.etapaDesde, ahora);
  return t === 'ahora mismo' ? 'recién llegó a esta etapa' : `${t} en esta etapa`;
}

/** «400000» → «US$400 mil»; «1200000» → «US$1.2 M». Lo que no es un número se deja como lo escribieron. */
export function presupuestoCorto(crudo: string | undefined): string | null {
  const texto = crudo?.trim();
  if (!texto) return null;
  const n = Number(texto.replace(/[$,\s]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return texto;
  if (n < 1_000) return `US$${n}`;
  // Por los miles ya redondeados: 999,600 es «US$1 M», no «US$1000 mil».
  const miles = Math.round(n / 1_000);
  return miles >= 1_000 ? `US$${Number((n / 1_000_000).toFixed(1))} M` : `US$${miles} mil`;
}

/** Los dos datos en gris junto al nombre: presupuesto y zona; si faltan, tipo y país. Máximo dos. */
export function datosDe(fila: FilaDeCartera): string[] {
  return [presupuestoCorto(fila.ficha.presupuesto_usd), fila.ficha.zona?.trim(), fila.tipo && legible(fila.tipo), fila.pais]
    .filter((d): d is string => Boolean(d))
    .slice(0, 2);
}
